/**
 * @file Logger conditionnel côté client, actif uniquement en développement.
 * @module packages.common.utils.clientLogger
 *
 * Partagé par le flow de réservation extrait du widget vers `packages/common`.
 * Contrairement à `widgetLogger` (no-op volontaire pour le bundle embarquable),
 * ce logger expose réellement la console afin que les avertissements et erreurs
 * du flow restent visibles en développement, quel que soit l'hôte (widget ou
 * site public). En production, toutes les méthodes sont inertes.
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Logger du flow de réservation, préfixant chaque message par `[Reservation]`.
 *
 * Quatre niveaux mappés sur la console : `error`, `warn`, `info` et `debug`
 * (ce dernier via `console.log`). Aucune méthode n'émet quoi que ce soit hors
 * développement, ce qui évite de polluer la console des sites hôtes en production.
 *
 * @property {Function} error - Log d'erreur (console.error)
 * @property {Function} warn - Log d'avertissement (console.warn)
 * @property {Function} info - Log d'information (console.info)
 * @property {Function} debug - Log de debug (console.log)
 */
export const logger = {
  /**
   * Log une erreur (uniquement en développement).
   * @param {string} message - Message à logger
   * @param {...any} args - Arguments supplémentaires
   */
  error: (message, ...args) => {
    if (isDevelopment) {
      console.error(`[Reservation] ${message}`, ...args);
    }
  },

  /**
   * Log un avertissement (uniquement en développement).
   * @param {string} message - Message à logger
   * @param {...any} args - Arguments supplémentaires
   */
  warn: (message, ...args) => {
    if (isDevelopment) {
      console.warn(`[Reservation] ${message}`, ...args);
    }
  },

  /**
   * Log une information (uniquement en développement).
   * @param {string} message - Message à logger
   * @param {...any} args - Arguments supplémentaires
   */
  info: (message, ...args) => {
    if (isDevelopment) {
      console.info(`[Reservation] ${message}`, ...args);
    }
  },

  /**
   * Log un message de debug (uniquement en développement).
   * @param {string} message - Message à logger
   * @param {...any} args - Arguments supplémentaires
   */
  debug: (message, ...args) => {
    if (isDevelopment) {
      console.log(`[Reservation] ${message}`, ...args);
    }
  },
};
